import { getApiUrl } from "@/config/runtime-env";
import { getAccessToken } from "@/utils/tokenService";

/**
 * Marker for "a super admin is acting as this counselor". Written by the
 * /impersonate page after a one-time code is exchanged for a token, read by the
 * banner, and cleared on exit, expiry or any ordinary login (see saveTokens).
 */
export const IMPERSONATION_KEY = "gyanplug_impersonation";

export interface ImpersonationInfo {
  sessionId: string;
  targetName: string | null;
  actorName: string | null;
  /** ISO timestamp the impersonation token stops working. */
  expiresAt: string;
}

export function getImpersonation(): ImpersonationInfo | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationInfo) : null;
  } catch {
    return null;
  }
}

export function setImpersonation(info: ImpersonationInfo): void {
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(info));
}

export function clearImpersonation(): void {
  localStorage.removeItem(IMPERSONATION_KEY);
}

const unwrap = (body: any) => body?.data ?? body;

export interface ExchangeResponse {
  accessToken: string;
  expiresAt: string;
  sessionId: string;
  user: Record<string, any>;
  impersonatedBy: { id: string; name: string | null };
}

/** Swap the one-time code from the link for a short-lived access token. */
export async function exchangeImpersonationCode(code: string): Promise<ExchangeResponse> {
  const res = await fetch(`${getApiUrl()}/impersonation/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const body = await res.json().catch(() => ({}));
  const data = unwrap(body);
  if (!res.ok || !data?.accessToken) {
    throw new Error(data?.message || body?.message || "This link is invalid, expired or already used.");
  }
  return data as ExchangeResponse;
}

/** Revoke the session server-side. Failure is fine — the token expires on its own. */
export async function endImpersonationOnServer(): Promise<void> {
  const token = getAccessToken();
  if (!token) return;
  try {
    await fetch(`${getApiUrl()}/impersonation/end`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
  } catch {
    // ignore
  }
}
